import type { FunnelAggregateResult } from '@/domain/metrics/aggregation/funnel-metrics';
import type { MetricResult } from '@/domain/metrics/contracts';
import { FINANCIAL_METRIC_KEYS, FINANCIAL_METRIC_UNITS, type FinancialMetricComparison, type FinancialMetricState, type FinancialScenarioView } from './contracts';
import type { MetricWarning } from '@/domain/metrics/contracts';

function toState(result: MetricResult): FinancialMetricState {
  if (result.status === 'known') return result.warnings.length > 0 ? 'partial' : 'known';
  return result.status;
}

function pick(funnel: FunnelAggregateResult, key: (typeof FINANCIAL_METRIC_KEYS)[number]): MetricResult {
  return funnel[key];
}

/**
 * SPEC-009 DF-158/plan §31 — builds a comparison of the base graph's funnel
 * aggregate against one scenario's, reusing `FunnelAggregateResult` values
 * as-is (no new formula, per §85). `base` and `scenario` are expected to
 * come from `runMetricsEngine(...).funnel` calls for the same graph with/
 * without the scenario override layer applied.
 */
export function buildFinancialView(
  base: FunnelAggregateResult,
  scenario: FunnelAggregateResult,
  scenarioLabel: { scenarioId?: string; scenarioName: string },
): FinancialScenarioView {
  const warnings: MetricWarning[] = [...scenario.warnings];
  const metrics: FinancialMetricComparison[] = FINANCIAL_METRIC_KEYS.map((key) => {
    const baseResult = pick(base, key);
    const scenarioResult = pick(scenario, key);
    const baseValue = baseResult.status === 'known' ? (baseResult.effectiveValue as number) : null;
    const scenarioValue = scenarioResult.status === 'known' ? (scenarioResult.effectiveValue as number) : null;
    const delta = baseValue !== null && scenarioValue !== null ? scenarioValue - baseValue : null;
    return {
      metric: key,
      unit: FINANCIAL_METRIC_UNITS[key],
      baseValue,
      scenarioValue,
      delta,
      state: toState(scenarioResult),
    };
  });

  return { scenarioId: scenarioLabel.scenarioId, scenarioName: scenarioLabel.scenarioName, metrics, warnings };
}
